import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth, getAnonUid, errMsg } from "@/context/AuthContext";
import { logError } from "@/lib/logger";

const DAY = 86400000;
const Ctx = createContext(null);

const pad = (n) => String(n).padStart(2, "0");
export const localKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
export const todayKey = () => localKey(new Date().toISOString());

const EMPTY = { profile: { name: "", dailySpend: 3, startDate: new Date().toISOString() }, cravings: [], challenges: {}, favorites: [], program: { done: [] } };

const notifyError = (e) => {
  logError("API error", e);
  if (e?.response?.status === 403) {
    toast.error(errMsg(e), { description: "Découvre nos formules pour débloquer ce contenu." });
  } else {
    toast.error("Connexion impossible", { description: "Tes données n'ont pas pu être enregistrées." });
  }
};

const toggleIn = (list, item) => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);

export function computeStats(state) {
  const cr = [...state.cravings].sort((a, b) => new Date(b.date) - new Date(a.date));
  const relapses = cr.filter((c) => c.outcome === "gave_in");
  const lastRelapse = relapses.length ? new Date(relapses[0].date) : new Date(state.profile.startDate);
  const streakMs = Math.max(0, Date.now() - lastRelapse.getTime());
  const resisted = cr.filter((c) => c.outcome === "resisted").length;
  const total = cr.length;
  const daysSinceStart = Math.max(1, Math.ceil((Date.now() - new Date(state.profile.startDate)) / DAY));
  const sugarFreeDays = Math.max(0, daysSinceStart - new Set(relapses.map((r) => localKey(r.date))).size);
  const t = todayKey();
  return {
    sorted: cr,
    streakDays: Math.floor(streakMs / DAY),
    streakHours: Math.floor((streakMs % DAY) / 3600000),
    resisted,
    total,
    successRate: total ? Math.round((resisted / total) * 100) : 0,
    sugarFreeDays,
    moneySaved: +(sugarFreeDays * (state.profile.dailySpend || 0)).toFixed(2),
    avgIntensity: total ? +(cr.reduce((a, c) => a + c.intensity, 0) / total).toFixed(1) : 0,
    today: cr.filter((c) => localKey(c.date) === t),
    daysSinceStart,
  };
}

export function AppProvider({ children }) {
  const { user, checking } = useAuth();
  const uid = user ? user.user_id : getAnonUid();
  const base = `${API}/users/${uid}`;
  const [state, setState] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (checking) return;
    try {
      const { data } = await axios.get(`${base}/state`);
      setState({ program: { done: [] }, ...data });
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  }, [base, checking]);

  useEffect(() => { load(); }, [load]);

  // Optimistic update helper: apply locally, call API, resync on failure.
  const mutate = useCallback(async (optimistic, request, onSuccess) => {
    if (optimistic) setState(optimistic);
    try {
      const { data } = await request();
      if (onSuccess) setState((s) => onSuccess(s, data));
    } catch (e) {
      notifyError(e);
      load();
    }
  }, [load]);

  const actions = useMemo(() => ({
    addCraving: (c) => mutate(null, () => axios.post(`${base}/cravings`, { note: "", strategy: "", ...c }), (s, data) => ({ ...s, cravings: [...s.cravings, data] })),
    deleteCraving: (id) => mutate((s) => ({ ...s, cravings: s.cravings.filter((c) => c.id !== id) }), () => axios.delete(`${base}/cravings/${id}`)),
    toggleFavorite: (id) => mutate((s) => ({ ...s, favorites: toggleIn(s.favorites, id) }), () => axios.post(`${base}/favorites/${id}`), (s, d) => ({ ...s, favorites: d.favorites })),
    joinChallenge: (id) => mutate((s) => ({ ...s, challenges: { ...s.challenges, [id]: { joinedAt: new Date().toISOString(), checked: [] } } }), () => axios.post(`${base}/challenges/${id}`), (s, d) => ({ ...s, challenges: d.challenges })),
    leaveChallenge: (id) => mutate((s) => { const ch = { ...s.challenges }; delete ch[id]; return { ...s, challenges: ch }; }, () => axios.delete(`${base}/challenges/${id}`), (s, d) => ({ ...s, challenges: d.challenges })),
    checkChallenge: (id) => {
      const t = todayKey();
      return mutate((s) => {
        const c = s.challenges[id];
        return c ? { ...s, challenges: { ...s.challenges, [id]: { ...c, checked: toggleIn(c.checked, t) } } } : s;
      }, () => axios.post(`${base}/challenges/${id}/check`, { date: t }), (s, d) => ({ ...s, challenges: d.challenges }));
    },
    toggleProgramDay: (day) => mutate((s) => ({ ...s, program: { ...s.program, done: toggleIn(s.program?.done || [], day) } }), () => axios.post(`${base}/program`, { day }), (s, d) => ({ ...s, program: d.program })),
    updateProfile: (p) => mutate((s) => ({ ...s, profile: { ...s.profile, ...p } }), () => axios.put(`${base}/profile`, p), (s, d) => d),
    resetAll: () => mutate(null, () => axios.post(`${base}/reset`), (s, d) => d),
  }), [base, mutate]);

  const stats = useMemo(() => computeStats(state), [state]);
  const value = useMemo(() => ({ state, stats, loading, reload: load, ...actions }), [state, stats, loading, load, actions]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useApp = () => useContext(Ctx);
